import { createContext, useContext, useEffect, useState } from 'react'

import { STORAGE_KEYS } from '@/constants'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
  activeTheme: Theme
}

const defaultAppTheme = 'dark'

const initialState: ThemeProviderState = {
  theme: defaultAppTheme,
  activeTheme: defaultAppTheme,
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = defaultAppTheme,
  storageKey = STORAGE_KEYS.THEME,
  ...props
}) => {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem(storageKey) as Theme) || defaultTheme)
  const [activeTheme, setActiveTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )
  useEffect(() => {
    const root = window.document.documentElement
    if (!root) return
    root.classList.remove('light', 'dark')
    if (activeTheme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const systemTheme = mediaQuery.matches ? 'dark' : 'light'
      root.classList.add(systemTheme)
      setTheme(systemTheme)
      const handleChange = (e: MediaQueryListEvent) => {
        const newSystemTheme = e.matches ? 'dark' : 'light'
        root.classList.remove('light', 'dark')
        root.classList.add(newSystemTheme)
        setTheme(newSystemTheme)
      }
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    } else {
      if (!root) return
      root.classList.add(theme)
    }
  }, [activeTheme])

  const value = {
    theme,
    activeTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setActiveTheme(theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)
  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}
